import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Play, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { PageContainer } from '../app/AppLayout';
import { useApi } from '../api/context';
import {
  queryKeys,
  useBlueprint,
  useDeployment,
  useDeploymentResources,
  useResourceTypeMap,
  useSecrets,
} from '../api/queries';
import { isTerminalRunStatus, type Run, type RunSummary } from '../api/types';
import { runStatusPresentation } from '../components/presentation';
import { useToast } from '../components/toast';
import { ActionButton, Dialog, ErrorMessage, Loading, StatusBadge } from '../components/ui';
import { useOptionalDock } from '../dock/context';
import { ToolWindowContent } from '../dock/ToolWindowContent';
import { ApplyDialog } from '../deployments/runDialogs';
import { durationMs, formatClock, formatDateTime, formatDuration, formatTime } from '../lib/format';
import { countActions } from '../lib/plan';
import { planBlockers, shortHash, type PlanBlocker } from '../lib/planReview';
import { compareHref } from '../lib/versionCompare';
import { MenuButton } from '../shell/Menu';
import { WaitingText } from '../networks/components';
import { AfterApplyCallout } from '../networks/runs/AfterApplyCallout';
import { NetworkRunPage } from '../networks/runs/NetworkRunPage';
import { isNetworkRun } from '../networks/shared';
import { strings } from '../strings';
import { useVariablesData, useVariablesScope } from '../variables/useVariablesData';
import { VariablesWindow } from '../variables/VariablesWindow';
import { ActivityWindow } from './ActivityWindow';
import { CancelRunButton } from './CancelRun';
import { LiveRun } from './LiveRun';
import { PlanReview } from './PlanReview';
import { RUN_KIND_ICON } from './RunSummaryTable';
import { useRunEventLog } from './useRunEventLog';

const r = strings.runPage;
const p = strings.planReview;

/**
 * The Variables window on a run page (spec 6.10.2): the run's deployment's current variables, filtered to the
 * resources of the run, with a note that they aren't a snapshot of the run.
 */
function RunVariables({ run }: { run: Run }) {
  const deployment = useDeployment(run.deploymentId, { enabled: !!run.deploymentId });
  const blueprint = useBlueprint(deployment.data?.data.blueprintId ?? '', !!deployment.data);
  const types = useResourceTypeMap();
  const scope = useVariablesScope(null, false);
  const data = useVariablesData({
    deployment: deployment.data?.data ?? null,
    types,
    scopeIds: scope.effective,
  });
  const changes = run.result?.plan?.changes;
  const runResources = changes ? changes.map((c) => c.resourceId) : null;
  if (!deployment.data) return null;
  return (
    <ToolWindowContent window="variables" badge={data.badge}>
      <VariablesWindow
        context="run"
        data={data}
        types={types}
        blueprintId={deployment.data.data.blueprintId}
        deployment={deployment.data.data}
        scope={scope.scope}
        effectiveScope={scope.effective}
        onScopeChange={scope.setScope}
        runResources={runResources}
        parameters={blueprint.data ? (blueprint.data.data.content.parameters ?? {}) : undefined}
      />
    </ToolWindowContent>
  );
}

/** A run as a run summary, for the Apply dialog's "Apply this plan" (counts from its own plan). */
function asSummary(run: Run, deploymentName: string, blueprintId: string): RunSummary {
  const counts = run.result?.plan ? countActions(run.result.plan.changes) : null;
  return {
    ...run,
    deploymentName,
    blueprintId,
    blueprintName: '',
    changes: counts
      ? { create: counts.create, update: counts.update, replace: counts.replace, delete: counts.delete }
      : null,
    warnings: null,
  };
}

/** `/runs/:id` (spec 6.5): the plan review of a finished plan, else the live run; a network run's own page. */
export function RunPage() {
  const { id = '' } = useParams();
  const api = useApi();
  const runQuery = useQuery({ queryKey: queryKeys.run(id), queryFn: () => api.getRun(id) });
  if (runQuery.data && isNetworkRun(runQuery.data)) return <NetworkRunPage key={id} run={runQuery.data} />;
  return <DeploymentRunPage key={id} id={id} />;
}

function DeploymentRunPage({ id }: { id: string }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const dock = useOptionalDock();
  const [dialog, setDialog] = useState<'apply' | 'snapshot' | null>(null);
  const [busy, setBusy] = useState(false);
  const runQuery = useQuery({ queryKey: queryKeys.run(id), queryFn: () => api.getRun(id) });
  const run = runQuery.data;
  const { log, stream } = useRunEventLog(id, run, (completed: Run) => {
    queryClient.setQueryData(queryKeys.run(id), completed);
    void queryClient.invalidateQueries({ queryKey: ['deployment', completed.deploymentId] });
    void queryClient.invalidateQueries({ queryKey: ['runs'] });
  });
  // A network run has no deployment (feature spec 17).
  const deployment = useDeployment(run?.deploymentId ?? '', { enabled: !!run?.deploymentId });
  const d = deployment.data?.data;
  const blueprint = useBlueprint(d?.blueprintId ?? '', !!d);
  const types = useResourceTypeMap();
  const secrets = useSecrets(run?.kind === 'plan' ? run.deploymentId : null);
  const boundPlan = useQuery({
    queryKey: queryKeys.run(run?.expectedPlanRunId ?? ''),
    queryFn: () => api.getRun(run!.expectedPlanRunId!),
    enabled: !!run?.expectedPlanRunId,
  });
  const versionDoc = useQuery({
    queryKey: queryKeys.version(d?.blueprintId ?? '', run?.blueprintVersion ?? 0),
    queryFn: () => api.getVersion(d!.blueprintId, run!.blueprintVersion!),
    enabled: !!d && !!run?.blueprintVersion && run.kind !== 'plan',
    staleTime: Infinity,
  });
  const deployedResources = useDeploymentResources(run?.kind === 'destroy' ? run.deploymentId : null);
  const siblings = useQuery({
    queryKey: [...queryKeys.runSummaries(run?.deploymentId ?? '', null), 'siblings'],
    queryFn: () => api.listRunSummaries(run!.deploymentId, {}, { limit: 50 }),
    enabled: !!run,
  });
  const terminalNow = run ? isTerminalRunStatus(run.status) : true;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (terminalNow) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [terminalNow]);

  if (runQuery.error) return <ErrorMessage error={runQuery.error} />;
  if (!run) return <Loading />;

  const terminal = isTerminalRunStatus(run.status);
  const status = !terminal && log.runStatus ? log.runStatus : run.status;
  const kindName = strings.status.runKind[run.kind];
  const KindIcon = RUN_KIND_ICON[run.kind] ?? Play;
  const latestVersion = blueprint.data?.data.latestVersion;
  const review =
    run.kind === 'plan' && (run.status === 'completed' || (run.status === 'failed' && !!run.result?.plan));
  const blockers: PlanBlocker[] = d ? planBlockers(run, d, latestVersion) : ['notCompleted'];
  const applyReason = d?.activeRunId ? p.reasons.runActive : blockers.length ? p.reasons[blockers[0]] : null;
  const rollback = siblings.data?.items.find((x) => x.parentRunId === run.id);
  const appliedVersion = d?.lastSuccessfulApply?.blueprintVersion ?? null;
  const lastEvent = log.events.length ? log.events[log.events.length - 1].timestamp : null;
  // The run as loaded may predate its start; the stream's first status event says when it started.
  const startedAt =
    run.startedAt ??
    (status !== 'pending' ? (log.events.find((e) => e.kind === 'run-status')?.timestamp ?? null) : null);

  const planAgain = async () => {
    setBusy(true);
    try {
      const created = await api.createRun(run.deploymentId, { kind: 'plan' });
      void queryClient.invalidateQueries({ queryKey: ['deployment', run.deploymentId] });
      void queryClient.invalidateQueries({ queryKey: ['runs'] });
      navigate(`/runs/${created.id}`);
    } catch (error) {
      toast.error(error);
    } finally {
      setBusy(false);
    }
  };
  const startApply = async (request: Parameters<typeof api.createRun>[1]) => {
    setBusy(true);
    try {
      const created = await api.createRun(run.deploymentId, request);
      void queryClient.invalidateQueries({ queryKey: ['deployment', run.deploymentId] });
      void queryClient.invalidateQueries({ queryKey: ['runs'] });
      setDialog(null);
      navigate(`/runs/${created.id}`);
    } catch (error) {
      toast.error(error);
    } finally {
      setBusy(false);
    }
  };
  const downloadJson = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(run, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `run-${run.id}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <PageContainer full>
      <header className="run-header">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <KindIcon aria-hidden className="size-5" />
            <h1 className="text-xl font-semibold">{kindName}</h1>
            <span data-testid="run-status">
              <StatusBadge presentation={runStatusPresentation[status]} />
            </span>
            {run.blueprintVersion !== null && (
              <span className="badge tone-neutral">{r.blueprintVersion(run.blueprintVersion)}</span>
            )}
            <span className="badge tone-neutral">{run.scope === 'vault' ? r.scopeVault : r.scopeFull}</span>
            {run.expectedPlanRunId && (
              <Link className="badge tone-neutral" to={`/runs/${run.expectedPlanRunId}`}>
                {r.boundTo(formatClock(boundPlan.data?.finishedAt ?? null))}
              </Link>
            )}
            {stream === 'live' && !terminal && (
              <span className="badge tone-success">
                <span aria-hidden className="status-dot bg-success" />
                {r.live}
              </span>
            )}
            {stream === 'reconnecting' && (
              <span className="badge tone-warning" role="status">
                {r.reconnecting(formatTime(lastEvent))}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-fg-muted">
            {r.deployment}{' '}
            <Link className="link underline" to={`/deployments/${run.deploymentId}`}>
              {d?.name ?? strings.common.dash}
            </Link>
            {' · '}
            {startedAt ? (
              <span title={formatDateTime(startedAt)}>
                {r.started(formatClock(startedAt), run.createdBy)}
              </span>
            ) : (
              <>
                {r.queued}
                {run.waitingFor && (
                  <>
                    {' · '}
                    <WaitingText waitingFor={run.waitingFor} />
                  </>
                )}
                {run.waitForRunId && !run.waitingFor && (
                  <>
                    {' · '}
                    <Link className="link underline" to={`/runs/${run.waitForRunId}`}>
                      {r.afterRun}
                    </Link>
                  </>
                )}
              </>
            )}
            {startedAt && (
              <>
                {' · '}
                {terminal
                  ? formatDuration(durationMs(startedAt, run.finishedAt))
                  : r.running(formatDuration(durationMs(startedAt, null, now)))}
              </>
            )}
            {run.kind === 'apply' && (
              <>
                {' · '}
                {r.policy(
                  run.onFailure === 'rollback'
                    ? strings.settings.rollback.toLowerCase()
                    : strings.settings.stop.toLowerCase(),
                )}
              </>
            )}
            {' · '}
            <button type="button" className="link underline" onClick={() => setDialog('snapshot')}>
              {r.snapshot}
            </button>
            {d &&
              appliedVersion !== null &&
              run.blueprintVersion !== null &&
              appliedVersion !== run.blueprintVersion && (
                <>
                  {' · '}
                  <Link
                    className="link underline"
                    to={compareHref(d.blueprintId, appliedVersion, run.blueprintVersion)}
                  >
                    {r.compare(appliedVersion, run.blueprintVersion)}
                  </Link>
                </>
              )}
            {run.triggeredBy === 'rollback' && run.parentRunId && (
              <>
                {' · '}
                {r.rollbackOf}{' '}
                <Link className="link underline" to={`/runs/${run.parentRunId}`}>
                  {r.parentRun}
                </Link>
              </>
            )}
            {rollback && (
              <>
                {' · '}
                {r.rolledBackBy}{' '}
                <Link className="link underline" to={`/runs/${rollback.id}`}>
                  {r.rollbackRun}
                </Link>
              </>
            )}
          </p>
        </div>
        <div className="run-actions">
          {review && (
            <>
              <ActionButton
                permission="operate"
                disabled={busy}
                icon={<RefreshCw aria-hidden className="size-4" />}
                onClick={() => void planAgain()}
              >
                {p.planAgain}
              </ActionButton>
              <ActionButton
                permission="operate"
                variant="primary"
                disabledReason={applyReason}
                icon={<Play aria-hidden className="size-4" />}
                onClick={() => setDialog('apply')}
              >
                {p.applyThisPlan}
              </ActionButton>
            </>
          )}
          {!terminal && (status === 'pending' || status === 'running') && <CancelRunButton run={run} />}
          <MenuButton
            buttonContent={<MoreHorizontal aria-hidden className="size-4" />}
            buttonLabel={p.moreActions}
            buttonTitle={p.moreActions}
            buttonClassName="btn btn-icon"
            menuLabel={p.moreActions}
            placement="below-end"
            entries={[
              {
                kind: 'action',
                id: 'copy',
                label: p.copyLink,
                onSelect: () =>
                  void navigator.clipboard
                    ?.writeText(window.location.href)
                    .then(() => toast.show({ tone: 'success', title: p.linkCopied }))
                    .catch(() => {}),
              },
              { kind: 'action', id: 'json', label: p.downloadJson, onSelect: downloadJson },
            ]}
          />
        </div>
      </header>

      {review ? (
        <PlanReview
          run={run}
          deployment={d}
          latestVersion={latestVersion}
          blockers={blockers}
          types={types}
          secrets={secrets.data}
          eventCount={log.events.length}
          onPlanAgain={() => void planAgain()}
          onShowLog={() => dock?.show('activity', { focus: true })}
        />
      ) : (
        <LiveRun
          run={run}
          status={status}
          log={log}
          deployment={d}
          boundPlan={boundPlan.data}
          versionDoc={versionDoc.data?.content}
          deployedResources={deployedResources.data}
          onPlanAgain={() => void planAgain()}
          afterSuccess={
            run.kind === 'apply' && d?.network?.mode === 'attached' ? (
              <AfterApplyCallout run={run} deployment={d} />
            ) : undefined
          }
        />
      )}

      {dialog === 'apply' && deployment.data && latestVersion !== undefined && (
        <ApplyDialog
          deployment={deployment.data}
          latestVersion={latestVersion}
          plan={asSummary(run, d?.name ?? '', d?.blueprintId ?? '')}
          busy={busy}
          onClose={() => setDialog(null)}
          onSubmit={(request) => void startApply(request)}
        />
      )}
      {dialog === 'snapshot' && (
        <Dialog title={r.snapshotTitle} onClose={() => setDialog(null)} wide>
          <p className="mb-2 text-sm text-fg-muted">{r.snapshotHash(shortHash(run.settingsHash))}</p>
          <pre className="max-h-[60vh] overflow-auto rounded-md border border-line bg-surface-2 p-3 font-mono text-xs">
            {JSON.stringify(run.settings, null, 2)}
          </pre>
        </Dialog>
      )}
      <RunVariables run={run} />
      <ToolWindowContent window="activity" badge={{ live: !terminal }}>
        <ActivityWindow runId={run.id} deployment={null} onRunPage />
      </ToolWindowContent>
    </PageContainer>
  );
}
